import React, { useState, useEffect } from 'react';
import {
  Settings,
  Plus,
  Trash2,
  GripVertical,
  Save,
  RefreshCw,
  Info,
  Key,
  Globe,
  Link,
  AlertTriangle,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

// CSS für Drag & Drop Effekte
const dragDropStyles = `
  .dragging {
    opacity: 0.5 !important;
    transform: scale(0.95) !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2) !important;
  }

  .drag-over {
    border-color: #3b82f6 !important;
    background-color: #eff6ff !important;
    transform: scale(1.02) !important;
  }

  .parameter-item {
    user-select: none;
  }
`;

// Style-Tag hinzufügen falls noch nicht vorhanden
if (typeof document !== 'undefined' && !document.getElementById('oci-drag-drop-styles')) {
  const style = document.createElement('style');
  style.id = 'oci-drag-drop-styles';
  style.textContent = dragDropStyles;
  document.head.appendChild(style);
}

const OciParameterEditor = ({ shopId, user, onClose, onSave }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Toast-Notifications State
  const [notifications, setNotifications] = useState([]);

  // Notification-Funktionen
  const showNotification = (message, type = 'info', duration = 4000) => {
    const id = Math.random().toString(36).substr(2, 9);
    const notification = { id, message, type, timestamp: Date.now() };

    setNotifications(prev => [...prev, notification]);

    // Auto-remove nach duration
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Helper-Funktionen für verschiedene Notification-Typen
  const showSuccess = (message) => showNotification(message, 'success', 3000);
  const showError = (message) => showNotification(message, 'error', 5000);
  const showWarning = (message) => showNotification(message, 'warning', 4000);

  // Standard Parameter-Typen
  const parameterTypes = [
    { value: 'static', label: 'Statisch', icon: Globe, description: 'Fester Wert' },
    { value: 'credential', label: 'Anmeldedaten', icon: Key, description: 'Username/Password' },
    { value: 'dynamic', label: 'Dynamisch', icon: Link, description: 'Zur Laufzeit generiert' }
  ];

  // Standard-Vorlagen für neue Parameter
  const parameterTemplates = [
    { param_key: '~OkCode', param_value: 'ADDI', param_type: 'static', is_required: true },
    { param_key: '~TARGET', param_value: '_top', param_type: 'static', is_required: true },
    { param_key: '~CALLER', param_value: 'CTLG', param_type: 'static', is_required: true },
    { param_key: '~HTTP_CONTENT_CHARSET', param_value: 'UTF-8', param_type: 'static', is_required: false },
    { param_key: 'USERNAME', param_value: '{username}', param_type: 'credential', is_required: true },
    { param_key: 'PASSWORD', param_value: '{password}', param_type: 'credential', is_required: true },
    { param_key: 'HOOK_URL', param_value: '{hook_url}', param_type: 'dynamic', is_required: true }
  ];

  // Parameter laden
  const loadParameters = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/oci-shops/${shopId}/parameters`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setParameters(data.map(param => ({
          ...param,
          tempId: Math.random().toString(36).substr(2, 9) // Für React keys
        })));
      } else if (response.status === 404 || response.status === 400) {
        // Keine Parameter vorhanden - leeres Array
        setParameters([]);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading parameters:', error);
      showError('Fehler beim Laden der Parameter: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shopId) {
      loadParameters();
    }
  }, [shopId]);

  // Standard-Parameter erstellen
  const createDefaultParameters = async () => {
    try {
      setSaving(true);
      const response = await fetch(`/api/oci-shops/${shopId}/parameters/default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        await loadParameters();
        setHasChanges(false);
        showSuccess(t('admin.ociParams.defaultsCreated'));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Error creating default parameters:', error);
      showError(t('admin.ociParams.defaultsError', { error: error.message }));
    } finally {
      setSaving(false);
    }
  };

  // Neuen Parameter hinzufügen
  const addParameter = () => {
    const newParam = {
      tempId: Math.random().toString(36).substr(2, 9),
      param_key: '',
      param_value: '',
      param_type: 'static',
      is_required: false
    };
    setParameters([...parameters, newParam]);
    setHasChanges(true);
  };

  // Parameter löschen
  const removeParameter = (index) => {
    const newParameters = parameters.filter((_, i) => i !== index);
    setParameters(newParameters);
    setHasChanges(true);
  };

  // Parameter aktualisieren
  const updateParameter = (index, field, value) => {
    const newParameters = [...parameters];
    newParameters[index] = { ...newParameters[index], [field]: value };
    setParameters(newParameters);
    setHasChanges(true);
  };

  // Drag & Drop Funktionen - Verbessert
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Verbesserte visuelle Rückmeldung
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    // Nur setDragOverIndex(null) wenn wir wirklich das Element verlassen
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newParameters = [...parameters];
    const draggedParam = newParameters[draggedIndex];

    // Parameter entfernen und an neuer Position einfügen
    newParameters.splice(draggedIndex, 1);
    newParameters.splice(dropIndex, 0, draggedParam);

    setParameters(newParameters);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setHasChanges(true);
  };

  // Parameter speichern
  const saveParameters = async () => {
    try {
      setSaving(true);

      // Validierung
      for (let i = 0; i < parameters.length; i++) {
        const param = parameters[i];
        if (!param.param_key.trim()) {
          showWarning(t('admin.ociParams.keyRequired', { index: i + 1 }));
          return;
        }
      }

      const response = await fetch(`/api/oci-shops/${shopId}/parameters`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ parameters })
      });

      if (response.ok) {
        setHasChanges(false);
        if (onSave) onSave();
        showSuccess(t('admin.ociParams.saved'));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Error saving parameters:', error);
      showError(t('admin.ociParams.saveError', { error: error.message }));
    } finally {
      setSaving(false);
    }
  };

  // Parameter-Typ Icon
  const getTypeIcon = (type) => {
    const typeInfo = parameterTypes.find(t => t.value === type);
    return typeInfo ? typeInfo.icon : Globe;
  };

  // Toast-Notification Komponente
  const ToastNotification = ({ notification, onRemove }) => {
    const getNotificationStyle = (type) => {
      switch (type) {
        case 'success':
          return {
            lightClasses: 'bg-white border border-emerald-200 shadow-sm',
            darkClasses: 'bg-gradient-to-br from-emerald-600/20 via-green-600/20 to-emerald-700/20 border-emerald-400/30 shadow-[0_8px_32px_rgba(16,185,129,0.3)]',
            lightText: 'text-emerald-700',
            darkText: 'text-emerald-200',
            icon: CheckCircle,
            lightIconColor: 'text-emerald-600',
            darkIconColor: 'text-emerald-300'
          };
        case 'error':
          return {
            lightClasses: 'bg-white border border-red-200 shadow-sm',
            darkClasses: 'bg-gradient-to-br from-red-600/20 via-red-600/20 to-red-700/20 border-red-400/30 shadow-[0_8px_32px_rgba(239,68,68,0.3)]',
            lightText: 'text-red-700',
            darkText: 'text-red-200',
            icon: XCircle,
            lightIconColor: 'text-red-600',
            darkIconColor: 'text-red-300'
          };
        case 'warning':
          return {
            lightClasses: 'bg-white border border-amber-200 shadow-sm',
            darkClasses: 'bg-gradient-to-br from-amber-600/20 via-yellow-600/20 to-amber-700/20 border-amber-400/30 shadow-[0_8px_32px_rgba(245,158,11,0.3)]',
            lightText: 'text-amber-700',
            darkText: 'text-amber-200',
            icon: AlertTriangle,
            lightIconColor: 'text-amber-600',
            darkIconColor: 'text-amber-300'
          };
        default:
          return {
            lightClasses: 'bg-white border border-blue-200 shadow-sm',
            darkClasses: 'bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-blue-700/20 border-blue-400/30 shadow-[0_8px_32px_rgba(59,130,246,0.3)]',
            lightText: 'text-brand',
            darkText: 'text-blue-200',
            icon: Info,
            lightIconColor: 'text-brand',
            darkIconColor: 'text-blue-300'
          };
      }
    };

    const style = getNotificationStyle(notification.type);
    const IconComponent = style.icon;

    return (
      <div className="relative group/toast">
        {/* Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? `${style.darkClasses} backdrop-blur-2xl border`
          : style.lightClasses
        } rounded-2xl`}></div>
        <div className={isDarkMode ? 'absolute inset-0 bg-gradient-to-t from-white/5 via-transparent to-white/10 rounded-2xl' : 'hidden'}></div>

        <div className="relative p-4">
          <div className="flex items-start space-x-3">
            {/* Icon */}
            <div className="flex-shrink-0">
              <IconComponent className={`w-5 h-5 ${isDarkMode ? style.darkIconColor : style.lightIconColor}`} />
            </div>

            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${isDarkMode ? style.darkText : style.lightText}`}>
                {notification.message}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => onRemove(notification.id)}
              className={`flex-shrink-0 transition-colors duration-200 ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/30'} flex items-center justify-center p-4 z-50`}>
      {/* Toast-Notifications Container */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-[60] space-y-3 max-w-md">
          {notifications.map((notification) => (
            <ToastNotification
              key={notification.id}
              notification={notification}
              onRemove={removeNotification}
            />
          ))}
        </div>
      )}

      <div className="relative w-full max-w-5xl h-[90vh] flex flex-col">
        {/* Modal Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.6)] backdrop-blur-2xl'
          : 'glass-light-card shadow-lg'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        {/* Floating Glass Particles - dark only */}
        <div className={`absolute top-4 left-1/4 w-1 h-1 ${isDarkMode ? 'bg-white/20' : 'hidden'} rounded-full animate-pulse`}></div>
        <div className={`absolute top-6 right-1/3 w-0.5 h-0.5 ${isDarkMode ? 'bg-white/30' : 'hidden'} rounded-full animate-ping`}></div>

        <div className="relative flex flex-col h-full">
          {/* Header - Fixed */}
          <div className="relative flex-shrink-0">
            {/* Header Background */}
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-r from-purple-900/20 via-indigo-900/20 to-purple-900/20 border-b border-white/10 backdrop-blur-xl'
              : 'bg-white border-b border-slate-200'
            } rounded-t-3xl`}></div>
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-t from-white/5 to-white/15'
              : ''
            } rounded-t-3xl`}></div>

            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Settings Icon */}
                  <div className="relative group/icon">
                    <div className={isDarkMode ? 'absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm' : 'hidden'}></div>
                    <div className={`relative ${isDarkMode
                      ? 'bg-gradient-to-br from-purple-600/60 via-purple-700/50 to-purple-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                      : 'bg-brand/10'
                    } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                      <Settings className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                      <div className={isDarkMode ? 'absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm' : 'hidden'}></div>
                    </div>
                  </div>
                  <div>
                    <h2 className={`text-2xl font-bold ${isDarkMode
                      ? 'bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent drop-shadow-lg'
                      : 'text-slate-900'
                    }`}>{t('admin.ociParams.title')}</h2>
                    <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'} mt-1`}>{t('admin.ociParams.subtitle')}</p>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className={`relative group/close overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                    isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 p-2'
                  }`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : undefined}
                >
                  {/* Hover Overlay - dark only */}
                  <div
                    className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/close:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                    }}
                  ></div>

                  <div className={`relative z-10 flex items-center justify-center transition-all duration-300 ${
                    isDarkMode ? 'text-white p-2 drop-shadow-lg group-hover/close:drop-shadow-xl' : 'text-slate-600'
                  }`}>
                    <X className="w-4 h-4 transition-transform duration-300 group-hover/close:scale-110" />
                  </div>

                  {/* Wave Effect - dark only */}
                  <div className={`absolute inset-0 opacity-0 group-hover/close:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                    <div
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/close:translate-x-full transition-transform duration-800"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                      }}
                    ></div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {loading ? (
              <div className="text-center py-8">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDarkMode ? 'border-purple-400' : 'border-brand'} mx-auto`}></div>
                <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'} mt-4`}>{t('common.loading')}</p>
              </div>
            ) : (
              <>
                {/* Info-Box */}
                <div className="relative group/info mb-6">
                  {/* Background */}
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-blue-700/20 border border-blue-400/30 backdrop-blur-xl'
                    : 'bg-brand/5 border border-brand/20'
                  } rounded-2xl`}></div>
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                    : ''
                  } rounded-2xl`}></div>

                  <div className="relative p-4">
                    <div className="flex items-start space-x-3">
                      <Info className={`w-5 h-5 ${isDarkMode ? 'text-blue-300' : 'text-brand'} mt-0.5`} />
                      <div className="text-sm">
                        <p className={`${isDarkMode ? 'text-white drop-shadow-sm' : 'text-slate-900'} font-medium`}>Parameter-Typen:</p>
                        <ul className={`${isDarkMode ? 'text-blue-200 drop-shadow-sm' : 'text-slate-600'} mt-2 space-y-1`}>
                          <li><strong className={`${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Statisch:</strong> Feste Werte (z.B. ~OkCode=ADDI)</li>
                          <li><strong className={`${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Anmeldedaten:</strong> Verwenden {'{username}'} oder {'{password}'}</li>
                          <li><strong className={`${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Dynamisch:</strong> Zur Laufzeit generiert (z.B. {'{hook_url}'})</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {/* Add Parameter Button */}
                  <button
                    onClick={addParameter}
                    className={`relative group/add overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                      isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-4 py-2 flex items-center font-medium shadow-sm'
                    }`}
                    style={isDarkMode ? {
                      background: 'linear-gradient(135deg, rgba(34,197,94,0.7) 0%, rgba(21,128,61,0.7) 50%, rgba(22,101,52,0.7) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    } : undefined}
                  >
                    {/* Hover Overlay - dark only */}
                    <div
                      className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/add:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                      }}
                    ></div>

                    <div className={`relative z-10 flex items-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'px-4 py-2 drop-shadow-lg group-hover/add:drop-shadow-xl' : ''}`}>
                      <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/add:scale-110" />
                      {t('admin.ociParams.addParam')}
                    </div>

                    {/* Wave Effect - dark only */}
                    <div className={`absolute inset-0 opacity-0 group-hover/add:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                      <div
                        className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/add:translate-x-full transition-transform duration-800"
                        style={{
                          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                        }}
                      ></div>
                    </div>
                  </button>

                  {parameters.length === 0 && (
                    <button
                      onClick={createDefaultParameters}
                      disabled={saving}
                      className={`relative group/default overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${
                        isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 flex items-center font-medium'
                      }`}
                      style={isDarkMode ? {
                        background: 'linear-gradient(135deg, rgba(147,51,234,0.7) 0%, rgba(126,34,206,0.7) 50%, rgba(107,33,168,0.7) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 8px 32px rgba(147,51,234,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                      } : undefined}
                    >
                      {/* Hover Overlay - dark only */}
                      <div
                        className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/default:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                        style={{
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                        }}
                      ></div>

                      <div className={`relative z-10 flex items-center font-medium transition-all duration-300 ${isDarkMode ? 'text-white px-4 py-2 drop-shadow-lg group-hover/default:drop-shadow-xl' : ''}`}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : 'transition-transform duration-300 group-hover/default:scale-110'}`} />
                        {t('admin.ociParams.createDefaults')}
                      </div>

                      {/* Wave Effect - dark only */}
                      <div className={`absolute inset-0 opacity-0 group-hover/default:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                        <div
                          className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/default:translate-x-full transition-transform duration-800"
                          style={{
                            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                          }}
                        ></div>
                      </div>
                    </button>
                  )}
                </div>

                {/* Parameter Liste */}
                {parameters.length === 0 ? (
                  <div className="relative group/empty">
                    {/* Background */}
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-br from-gray-600/20 via-gray-700/20 to-gray-800/20 border border-gray-400/30 backdrop-blur-xl'
                      : 'bg-white border border-slate-200'
                    } rounded-2xl`}></div>
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                      : ''
                    } rounded-2xl`}></div>

                    <div className="relative text-center py-12">
                      <Settings className={`w-12 h-12 ${isDarkMode ? 'text-white/60 drop-shadow-lg' : 'text-slate-400'} mx-auto mb-4`} />
                      <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white drop-shadow-sm' : 'text-slate-900'} mb-2`}>{t('admin.ociParams.empty')}</h3>
                      <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'}`}>{t('admin.ociParams.emptyHint')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {parameters.map((param, index) => {
                      const TypeIcon = getTypeIcon(param.param_type);

                      return (
                        <div
                          key={param.tempId || param.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDragEnter={(e) => handleDragEnter(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          className={`relative group/param cursor-move transition-all duration-200 ${
                            draggedIndex === index
                              ? 'opacity-50 scale-95 z-10'
                              : dragOverIndex === index
                              ? 'scale-105 z-20'
                              : 'hover:scale-[1.01]'
                          }`}
                        >
                          {/* Parameter Card Background */}
                          <div className={`absolute inset-0 rounded-2xl border transition-all duration-300 ${
                            draggedIndex === index
                              ? 'bg-gradient-to-br from-purple-600/30 via-purple-700/30 to-purple-800/30 border-purple-400/50 shadow-[0_12px_40px_rgba(147,51,234,0.4)] backdrop-blur-2xl'
                              : dragOverIndex === index
                              ? 'bg-gradient-to-br from-blue-600/30 via-blue-700/30 to-blue-800/30 border-blue-400/50 shadow-[0_12px_40px_rgba(59,130,246,0.4)] backdrop-blur-2xl'
                              : isDarkMode
                              ? 'bg-gradient-to-br from-gray-800/20 via-gray-700/20 to-gray-800/20 border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)] group-hover/param:shadow-[0_12px_40px_rgba(0,0,0,0.6)] backdrop-blur-2xl'
                              : 'bg-white border-slate-200 shadow-sm group-hover/param:shadow-md'
                          }`}></div>
                          <div className={`absolute inset-0 ${isDarkMode
                            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                            : ''
                          } rounded-2xl`}></div>

                          <div className="relative p-4">
                            <div className="flex items-center space-x-4">
                              {/* Drag Handle */}
                              <div className={`cursor-grab active:cursor-grabbing transition-colors ${isDarkMode
                                ? 'text-white/60 hover:text-purple-300 drop-shadow-lg'
                                : 'text-slate-400 hover:text-brand'
                              }`}>
                                <GripVertical className="w-5 h-5" />
                              </div>

                              {/* Reihenfolge */}
                              <div className={`flex-shrink-0 w-8 h-8 ${isDarkMode
                                ? 'bg-gradient-to-br from-purple-600/60 via-purple-700/50 to-purple-800/60 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(147,51,234,0.3)] drop-shadow-lg'
                                : 'bg-brand/10 border border-brand/20'
                              } text-${isDarkMode ? 'white' : 'brand'} rounded-full flex items-center justify-center text-sm font-medium`}>
                                <span className={isDarkMode ? 'text-white' : 'text-brand'}>{index + 1}</span>
                              </div>

                              {/* Parameter Key */}
                              <div className="flex-1 min-w-0">
                                <label className={`block text-xs font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-1`}>{t('admin.ociParams.paramKey')}</label>
                                <div className="relative">
                                  <div className={`absolute inset-0 ${isDarkMode
                                    ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                                    : ''
                                  } rounded-2xl`}></div>
                                  <input
                                    type="text"
                                    value={param.param_key}
                                    onChange={(e) => updateParameter(index, 'param_key', e.target.value)}
                                    placeholder="z.B. ~OkCode"
                                    className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                                      ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                                      : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                                    }`}
                                  />
                                </div>
                              </div>

                              {/* Parameter Value */}
                              <div className="flex-1 min-w-0">
                                <label className={`block text-xs font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-1`}>{t('admin.ociParams.paramValue')}</label>
                                <div className="relative">
                                  <div className={`absolute inset-0 ${isDarkMode
                                    ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                                    : ''
                                  } rounded-2xl`}></div>
                                  <input
                                    type="text"
                                    value={param.param_value}
                                    onChange={(e) => updateParameter(index, 'param_value', e.target.value)}
                                    placeholder="z.B. ADDI oder {username}"
                                    className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                                      ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                                      : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                                    }`}
                                  />
                                </div>
                              </div>

                              {/* Parameter Type */}
                              <div className="flex-shrink-0 w-32">
                                <label className={`block text-xs font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-1`}>{t('admin.ociParams.paramType')}</label>
                                <div className="relative">
                                  <div className={`absolute inset-0 ${isDarkMode
                                    ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                                    : ''
                                  } rounded-2xl`}></div>
                                  <select
                                    value={param.param_type}
                                    onChange={(e) => updateParameter(index, 'param_type', e.target.value)}
                                    className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                                      ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white'
                                      : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800'
                                    }`}
                                  >
                                    {parameterTypes.map(type => (
                                      <option key={type.value} value={type.value} className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-slate-800'}`}>
                                        {type.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* Required Checkbox */}
                              <div className="flex items-center pt-5">
                                <label className="flex items-center space-x-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={param.is_required}
                                    onChange={(e) => updateParameter(index, 'is_required', e.target.checked)}
                                    className={`rounded ${isDarkMode
                                      ? 'border-white/30 bg-transparent text-purple-500 focus:ring-purple-400/50'
                                      : 'border-slate-300 bg-white text-brand focus:ring-brand/40'
                                    } focus:ring-offset-0`}
                                  />
                                  <span className={`${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} whitespace-nowrap`}>{t('admin.ociParams.required')}</span>
                                </label>
                              </div>

                              {/* Type Icon */}
                              <div className="flex-shrink-0">
                                <TypeIcon className={`w-5 h-5 ${isDarkMode ? 'text-white/60 drop-shadow-lg' : 'text-slate-400'}`} />
                              </div>

                              {/* Delete Button */}
                              <button
                                onClick={() => removeParameter(index)}
                                className={`relative group/delete overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.05] active:scale-[0.95] ${
                                  isDarkMode ? '' : 'bg-slate-100 hover:bg-red-50 p-1'
                                }`}
                                style={isDarkMode ? {
                                  background: 'linear-gradient(135deg, rgba(71,85,105,0.4) 0%, rgba(75,85,99,0.4) 50%, rgba(71,85,105,0.4) 100%)',
                                  backdropFilter: 'blur(16px)',
                                  border: '1px solid rgba(255,255,255,0.2)',
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                                } : undefined}
                              >
                                {/* Hover Overlay - dark only */}
                                <div
                                  className={`absolute inset-0 rounded-xl transition-all duration-300 opacity-0 group-hover/delete:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                                  style={{
                                    background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.2) 50%, rgba(239,68,68,0.15) 100%)'
                                  }}
                                ></div>

                                <div className={`relative z-10 flex items-center justify-center transition-all duration-300 ${isDarkMode ? 'p-1 drop-shadow-lg group-hover/delete:drop-shadow-xl' : ''}`}>
                                  <Trash2 className={`w-4 h-4 transition-all duration-300 ${
                                    isDarkMode ? 'text-white/70 group-hover/delete:text-red-300' : 'text-slate-500 group-hover/delete:text-red-600'
                                  } group-hover/delete:scale-110`} />
                                </div>

                                {/* Wave Effect - dark only */}
                                <div className={`absolute inset-0 opacity-0 group-hover/delete:opacity-100 transition-opacity duration-400 ${isDarkMode ? '' : 'hidden'}`}>
                                  <div
                                    className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/delete:translate-x-full transition-transform duration-600"
                                    style={{
                                      background: 'linear-gradient(90deg, transparent 0%, rgba(239,68,68,0.2) 50%, transparent 100%)'
                                    }}
                                  ></div>
                                </div>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Zusätzlicher Abstand am Ende für bessere Scrollbarkeit */}
                <div className="h-4"></div>
              </>
            )}
          </div>

          {/* Footer - Fixed */}
          <div className="relative flex-shrink-0">
            {/* Footer Background */}
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-r from-gray-900/20 via-gray-800/20 to-gray-900/20 border-t border-white/10 backdrop-blur-xl'
              : 'bg-white border-t border-slate-200'
            } rounded-b-3xl`}></div>
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-b from-white/5 to-white/15'
              : ''
            } rounded-b-3xl`}></div>

            <div className="relative px-6 py-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className={`text-sm ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'} flex items-center space-x-4`}>
                  {parameters.length > 0 && (
                    <>
                      <span className="flex items-center">
                        <Settings className="w-4 h-4 mr-1" />
                        {parameters.length} {t('admin.ociParams.parameter')}
                      </span>
                      <span className="flex items-center">
                        <GripVertical className="w-4 h-4 mr-1" />
                        {t('admin.ociParams.dragDrop')}
                      </span>
                    </>
                  )}
                  {hasChanges && (
                    <span className={`flex items-center ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {t('admin.ociParams.unsavedChanges')}
                    </span>
                  )}
                </div>
                <div className="flex space-x-3 w-full sm:w-auto">
                  {/* Cancel Button */}
                  <button
                    onClick={onClose}
                    className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] flex-1 sm:flex-none ${
                      isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 font-medium'
                    }`}
                    style={isDarkMode ? {
                      background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    } : undefined}
                  >
                    {/* Hover Overlay - dark only */}
                    <div
                      className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                      }}
                    ></div>

                    <div className={`relative z-10 flex items-center justify-center font-medium transition-all duration-300 ${
                      isDarkMode ? 'text-white px-6 py-2 drop-shadow-lg group-hover/cancel:drop-shadow-xl' : ''
                    }`}>
                      {t('common.cancel')}
                    </div>

                    {/* Wave Effect - dark only */}
                    <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                      <div
                        className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                        style={{
                          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                        }}
                      ></div>
                    </div>
                  </button>

                  {(parameters.length > 0 || hasChanges) && (
                    <button
                      onClick={saveParameters}
                      disabled={saving || !hasChanges}
                      className={`relative group/save overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 flex-1 sm:flex-none ${
                        isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-6 py-2 flex items-center justify-center font-medium shadow-sm'
                      }`}
                      style={isDarkMode ? {
                        background: 'linear-gradient(135deg, rgba(34,197,94,0.7) 0%, rgba(21,128,61,0.7) 50%, rgba(22,101,52,0.7) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                      } : undefined}
                    >
                      {/* Hover Overlay - dark only */}
                      <div
                        className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/save:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                        style={{
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                        }}
                      ></div>

                      <div className={`relative z-10 flex items-center justify-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'px-6 py-2 drop-shadow-lg group-hover/save:drop-shadow-xl' : ''}`}>
                        <Save className={`w-4 h-4 mr-2 ${saving ? 'animate-pulse' : 'transition-transform duration-300 group-hover/save:scale-110'}`} />
                        {saving ? t('common.saving') : hasChanges && parameters.length === 0 ? t('admin.ociParams.deleteSave') : t('common.save')}
                      </div>

                      {/* Wave Effect - dark only */}
                      <div className={`absolute inset-0 opacity-0 group-hover/save:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                        <div
                          className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/save:translate-x-full transition-transform duration-800"
                          style={{
                            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                          }}
                        ></div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OciParameterEditor;
